import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Text, Stack, Inline } from '@atlaskit/primitives/compiled';
import { Field } from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import Range from '@atlaskit/range';

import type {
	ConfluenceLength,
	ConfluenceTone,
} from './__generated__/CustomizePanelUpdateAudioPreferenceMutation.graphql.ts';

const i18n = defineMessages({
	audioDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration-label',
		defaultMessage: 'Roughly how long is the audio?',
		description: 'Label for a Select component used for selecting audio duration',
	},
	shortDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration.short',
		defaultMessage: 'Short',
		description: 'Label for a shorter length audio option',
	},
	mediumDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration.medium',
		defaultMessage: 'Medium',
		description: 'Label for a medium length audio option',
	},
	longDurationLabel: {
		id: 'audio.audio-popup.customize-tab.duration.long',
		defaultMessage: 'Long',
		description: 'Label for a longer length audio option',
	},
});

interface DurationOption {
	label: keyof typeof i18n;
	value: ConfluenceLength;
}

const durationOptions: DurationOption[] = [
	{
		label: 'shortDurationLabel',
		value: 'SHORT' as ConfluenceLength,
	},
	{
		label: 'mediumDurationLabel',
		value: 'MEDIUM' as ConfluenceLength,
	},
	{
		label: 'longDurationLabel',
		value: 'LONG' as ConfluenceLength,
	},
];

const styles = cssMap({
	durationSliderWrapper: {
		marginTop: token('space.negative.100'),
	},
	slider: {
		paddingInlineEnd: token('space.100'),
	},
});

interface DurationSelectorProps {
	onPreferenceUpdate: (
		updatedPreferences: Partial<{
			length: ConfluenceLength;
			tone: ConfluenceTone;
			playbackSpeed: number;
		}>,
	) => void;
	defaultValue: ConfluenceLength | null;
}

// We don't want to use real drag-and-drop but instead prevent dragging from starting on any parent
const onDragStart = (e: React.DragEvent<HTMLElement>) => {
	e.stopPropagation();
	e.preventDefault();
};

export const DurationSelector = ({ onPreferenceUpdate, defaultValue }: DurationSelectorProps) => {
	const { formatMessage } = useIntl();

	return (
		<Field
			name="length"
			label={formatMessage(i18n.audioDurationLabel)}
			id="audio-duration-field"
			defaultValue={defaultValue}
		>
			{({ fieldProps: { value: customLength, onChange } }) => {
				// Use the field value if available, otherwise fall back to defaultValue
				const currentLength = customLength || defaultValue;
				const rangeValue = Math.max(
					0,
					durationOptions.findIndex((option) => option.value === currentLength),
				);

				const handleRangeChange = (newRangeValue: number) => {
					const index = Math.round(newRangeValue);
					const selectedOption = durationOptions[index].value;
					onChange(selectedOption);

					onPreferenceUpdate({
						length: selectedOption,
					});
				};

				const currentOption = durationOptions[rangeValue];
				const currentValueText = currentOption ? formatMessage(i18n[currentOption.label]) : '';

				return (
					<Box xcss={styles.durationSliderWrapper}>
						<Stack grow="fill">
							<Box xcss={styles.slider}>
								<Range
									type="range"
									step={1}
									min={0}
									max={durationOptions.length - 1}
									value={rangeValue}
									onChange={handleRangeChange}
									onDragStart={onDragStart}
									draggable // Explicitly make draggable to intercept event and prevent any parent from dragging
									testId="duration-range-slider"
									aria-labelledby="audio-duration-field-label"
									aria-valuetext={currentValueText}
									aria-valuenow={rangeValue}
								/>
							</Box>
							<Inline spread="space-between" grow="fill">
								{durationOptions.map((option, index) => (
									<Text
										key={option.value}
										size="small"
										color={rangeValue === index ? 'color.text' : 'color.text.subtlest'}
										weight={rangeValue === index ? 'bold' : 'regular'}
									>
										{formatMessage(i18n[option.label])}
									</Text>
								))}
							</Inline>
						</Stack>
					</Box>
				);
			}}
		</Field>
	);
};
